(ns web3.view
  (:refer-clojure :exclude [rem])
  (:require [reagent.ratom :as ratom]
            [reagent.core :as r]
            [reagent.impl.component :refer [dash-to-camel]]
            [re-frame.db :as db]
            [goog.object :as gobj]
            ["react" :as react]
            ["react-dom" :refer [createPortal]]
            [web3.utils :as utils]
            [web3.react :as web3-react])
  (:require-macros [web3.view :as v]))

(def ^:dynamic *notify-watches?* true)

(defonce _
         (let [notify-w ratom/notify-w]
           (set! ratom/notify-w
                 (fn [this old new]
                   (when (true? *notify-watches?*)
                     (notify-w this old new))))))


;; web3.react cannot depend on reagent, because reagent uses the
;; old createReactClass utility instead of React.Component,
;; so we define these lookup methods here:

(defmethod web3-react/component-lookup :props
  [this _ _]
  (r/props this))

(defmethod web3-react/component-lookup :state
  [this _ _]
  (r/state-atom this))

(defmethod web3-react/component-lookup :argv
  [this _ _]
  (r/argv this))

(defn swap-silently!
  "Swap a reactive atom, without causing dependent components to re-render."
  [& args]
  (v/silently (apply swap! args)))

(defn on-unmount!
  "Register an unmount callback for `component`."
  [{:keys [state] :as component} key cb]
  (swap-silently! state assoc-in [::on-unmount key] cb))

(defn mounted?
  "Returns true if component has unmounted.

  (depends on `wrap-methods` in this namespace, and therefore only works for
   components created with the `defview` macro.)"
  [^js this]
  (not (.-unmounted this)))

(defn merge-props
  "Merge props, concatenating :class props and merging styles."
  [m1 m2]
  (merge m1
         m2
         (merge-with #(str %1 " " %2)
                     (select-keys m1 [:class])
                     (select-keys m2 [:class]))
         (merge-with merge
                     (select-keys m1 [:style])
                     (select-keys m2 [:style]))))

(defn partial-props
  "Partially apply props to a component. When the wrapped component is called,
   new props will be merged onto the previously applied props."
  [component the-extra-props]
  (fn [props & children]
    (let [[props children] (if (or (map? props) (nil? props)) [props children] [nil (cons props children)])]
      (into [component (merge the-extra-props props)] children))))

(comment
 ;; concat class strings
 (assert (= (merge-props {:class "a"}
                         {:class "b"})
            {:class "a b"}))
 ;; merge style maps
 (assert (= (merge-props {:style {:color "black"}}
                         {:style {:display "none"}})
            {:style {:color "black"
                     :display "none"}}))
 ;; otherwise merge normally
 (assert (= (merge-props {:x "a"}
                         {:x "b"})
            {:x "b"})))

(defn wrap-methods
  "Adds app-wide lifecycle behaviour to a methods map.
   (see comments for details)"
  [methods]
  (merge methods
         {:component-will-unmount
          (fn [{:keys [state] :as ^js component}]

            ;; fire callbacks registered via `on-unmount!`
            (doseq [f (vals (::on-unmount @state))]
              (f component))

            ;; call the lifecycle method that was passed to this function
            (when-let [f (get methods :component-will-unmount)]
              (f component))

            ;; set the `unmounted` javascript property of `this`
            ;; (enables the `mounted?` function to work)
            (set! (.-unmounted component) true)

            ;; remove this component from re-frame db
            ;; (some per-component state is indexed there)
            (swap-silently! db/app-db dissoc component))}))

(defn assoc-prev!
  [state k v]
  (swap-silently! state assoc-in [:prev k] v))

(defn set-prev-props!
  [{:keys [state props]} [_ next-props]]
  (assoc-prev! state :props props))

(defn prev
  []
  (some-> (r/current-component)
          :state
          deref
          :prev))

(comment
 (let [last-observed-value (atom nil)]
   (def x (ratom/atom 0))
   (ratom/run!
    (reset! last-observed-value @x)
    (prn :observed-value @x))
   (swap! x inc)
   (assert (= @last-observed-value 1))
   (swap-silently! x inc)
   (assert (= @last-observed-value 1))
   (assert (= @x 2))))

(def portal-element (js/document.getElementById "portal"))

(defn portal [form]
  (createPortal (r/as-element form) portal-element))

(defn class-get [view k]
  (-> (or (gobj/get view "prototype")
          (gobj/get view "__proto__"))
      (gobj/get (dash-to-camel k))))

(defn find-or-insert-id
  ([id] (find-or-insert-id :div id))
  ([el-type id]
   (or (.getElementById js/document id)
       (let [element (doto (.createElement js/document (name el-type))
                       (.setAttribute "id" id))]
         (.appendChild js/document.body element)
         element))))